/* eslint-disable parog-web/no-glint-expect-error-directive */
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import formatDate from 'ember-intl/helpers/format-date';
import eq from 'ember-truth-helpers/helpers/eq';
import type { DmpDocumentsSearchParams } from 'parog-web/cnam/types';
import { DocumentStatus, DocumentVisibility } from 'parog-web/cnam/types';
import DatePicker from 'parog-web/components/date-picker';
import DatetimePicker from 'parog-web/components/datetime-picker';
import Webpsurls from 'parog-web/components/documents-retrieve-access-form/search/webpsurls';
import type DmpConsultationContext from 'parog-web/contexts/dmp-consultation-context';
import type DmpDocumentMetadata from 'parog-web/models/dmp-document-metadata';
import dmpDocumentTypeCodes from 'parog-web/utils/dmp-document-type-codes';
import formatToISO8601Date from 'parog-web/utils/format-to-iso8601-date';
import Checkbox from 'ui-kit/components/checkbox';
import LinkButton from 'ui-kit/components/link-button';
import PageContainer from 'ui-kit/components/page-container';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';

interface Args {
  context: DmpConsultationContext;
}

export default class DocumentsRetrieveAccessFormSearchComponent extends Component<Args> {
  get dmpDocumentTypeCodes(): Array<unknown> {
    return dmpDocumentTypeCodes;
  }

  get hasDatesError() {
    return (
      this.serviceStartTimeFromDate &&
      this.serviceStopTimeToDate &&
      this.serviceStartTimeFromDate > this.serviceStopTimeToDate
    );
  }

  get lastSubmissionStartTimeFrom(): Date | undefined {
    const lastSearchDateString =
      this.args.context.medic.lastDmpDocumentsSearchDate;
    return lastSearchDateString ? new Date(lastSearchDateString) : undefined;
  }

  get serviceStartTimeFromDate(): Date | undefined {
    return this.args.context.searchParams.serviceStartTimeFrom
      ? new Date(this.args.context.searchParams.serviceStartTimeFrom)
      : undefined;
  }

  get serviceStopTimeToDate(): Date | undefined {
    return this.args.context.searchParams.serviceStopTimeTo
      ? new Date(this.args.context.searchParams.serviceStopTimeTo)
      : undefined;
  }

  get submissionStartTimeFromDate(): Date | undefined {
    return this.args.context.searchParams.submissionStartTimeFrom
      ? new Date(this.args.context.searchParams.submissionStartTimeFrom)
      : undefined;
  }

  get title(): string {
    return `Recherche de documents DMP${
      this.args.context.isSecretConnection
        ? ' - Connexion secrète en cours'
        : ''
    }`;
  }

  get typeCodesAsStr(): string {
    return this.args.context.searchParams.typeCodes.join(',');
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);
  }

  getClassCodeDisplayName(classCode: string): string {
    // Loop through DMP
    const dmpClass = dmpDocumentTypeCodes.find(
      dmpClass => dmpClass.classCode == classCode
    );
    return dmpClass ? dmpClass.displayName : classCode;
  }

  getStatusString(statuses: Array<DocumentStatus>): string {
    const status: Array<string> = [];

    if (statuses.includes(DocumentStatus.DEPRECATED)) {
      status.push('ancienne version obsolète');
    }

    if (statuses.includes(DocumentStatus.ARCHIVED)) {
      status.push('archivé');
    }

    return status.join(', ');
  }

  getVisibilityString(confidentialities: Array<DocumentVisibility>): string {
    const visibility: Array<string> = [];

    if (confidentialities.includes(DocumentVisibility.HIDDEN_TO_PATIENT)) {
      visibility.push('non visible par le patient');
    }

    if (
      confidentialities.includes(
        DocumentVisibility.HIDDEN_TO_HEALTHCARE_PROFESSIONAL
      )
    ) {
      visibility.push('masqué aux professionnels de santé');
    }

    if (
      confidentialities.includes(
        DocumentVisibility.HIDDEN_TO_LEGAL_REPRESENTATIVES
      )
    ) {
      visibility.push('non visible par les représentants légaux');
    }

    return visibility.join(', ');
  }

  onChange = (
    key: keyof DmpDocumentsSearchParams,
    value: DmpDocumentsSearchParams[keyof DmpDocumentsSearchParams]
  ): void => {
    let serializedValue;

    if (key == 'serviceStartTimeFrom' || key == 'serviceStopTimeTo') {
      serializedValue = value ? formatToISO8601Date(value as Date) : undefined;
    } else if (key == 'submissionStartTimeFrom') {
      serializedValue = value ? (value as Date).toISOString() : undefined;
    }

    if (key == 'typeCodes' && value) {
      serializedValue = (value as string).split(',');
    }

    serializedValue ||= value;

    (this.args.context.searchParams as unknown as Record<string, unknown>)[
      key
    ] = serializedValue;
  };

  onSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    this.#setLastDmpDocumentsSearchDate(new Date());
    await this.args.context.transitionTo();
  };

  setCurrentDocumentMetadata = async (
    documentMetadata: DmpDocumentMetadata
  ): Promise<void> => {
    this.args.context.currentDocumentMetadata = documentMetadata;
    this.args.context.currentState =
      await this.args.context.calculateCurrentState();
  };

  useLastSubmissionStartTimeFrom = (): void => {
    this.onChange('submissionStartTimeFrom', this.lastSubmissionStartTimeFrom);
  };

  #setLastDmpDocumentsSearchDate = (value: Date) => {
    this.args.context.medic.lastDmpDocumentsSearchDate = value;
    this.args.context.medic.save();
  };

  <template>
    <PageContainer @title={{this.title}} @dontUseTextWidthLimit={{true}}>
      <:content>
        <div class='ui basic segments' data-test-documents-search>
          <div class='ui segment'>
            <div class='ui form'>
              <div class='ui dividing header'>Date de l'acte</div>
              <div class='two fields'>
                <div class='field'>
                  <DatePicker
                    @labelName='Début'
                    @selected={{this.serviceStartTimeFromDate}}
                    @onChange={{fn this.onChange 'serviceStartTimeFrom'}}
                  />
                </div>

                <div class='field'>
                  <DatePicker
                    @labelName='Fin'
                    @selected={{this.serviceStopTimeToDate}}
                    @onChange={{fn this.onChange 'serviceStopTimeTo'}}
                  />
                </div>
              </div>

              <div class='ui dividing header'>Ajouté depuis le</div>
              <div class='two fields'>
                <div class='field'>
                  <DatetimePicker
                    @selected={{this.submissionStartTimeFromDate}}
                    @onChange={{fn this.onChange 'submissionStartTimeFrom'}}
                  />
                </div>

                {{#if this.lastSubmissionStartTimeFrom}}
                  <div class='field'>
                    <button
                      type='button'
                      class='ui tiny button mt8'
                      {{! @glint-expect-error }}
                      onclick={{this.useLastSubmissionStartTimeFrom}}
                    >
                      Depuis ma dernière recherche
                    </button>
                  </div>
                {{/if}}
              </div>

              <div class='ui dividing header'>Filtres</div>
              <div class='two fields inline'>
                <div class='grouped fields'>
                  <div class='field'>
                    <Checkbox
                      @labelName='Archivés'
                      @checked={{@context.searchParams.archived}}
                      @onChange={{fn this.onChange 'archived'}}
                    />
                  </div>

                  <div class='field'>
                    <Checkbox
                      @labelName='Obsolètes'
                      @checked={{@context.searchParams.obsolete}}
                      @onChange={{fn this.onChange 'obsolete'}}
                    />
                  </div>
                </div>

                <div class='grouped fields'>
                  <div class='field'>
                    <Checkbox
                      @labelName='Cachés au patient'
                      @checked={{@context.searchParams.hiddenToPatient}}
                      @onChange={{fn this.onChange 'hiddenToPatient'}}
                    />
                  </div>

                  <div class='field'>
                    <Checkbox
                      @labelName='Cachés aux représentants'
                      @checked={{@context.searchParams.hiddenToLegalRepresentatives}}
                      @onChange={{fn
                        this.onChange
                        'hiddenToLegalRepresentatives'
                      }}
                    />
                  </div>

                  <div class='field'>
                    <Checkbox
                      @labelName='Cachés aux PS'
                      @checked={{@context.searchParams.hiddenToHealthcareProfessional}}
                      @onChange={{fn
                        this.onChange
                        'hiddenToHealthcareProfessional'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div class='ui dividing header'>Catégories</div>
              <div class='field'>
                <div
                  class='ui clearable multiple selection dropdown'
                  {{fuiDropdown onChange=(fn this.onChange 'typeCodes')}}
                >
                  <input type='hidden' value={{this.typeCodesAsStr}} />
                  <i class='dropdown icon'></i>
                  <div class='default text'>Filtrer par types de document</div>

                  <div class='menu'>
                    {{#each this.dmpDocumentTypeCodes as |classCode|}}

                      {{! @glint-expect-error }}
                      <div class='header'>{{classCode.displayName}}</div>

                      {{! @glint-expect-error }}
                      {{#each classCode.typeCodes as |typeCode|}}
                        <div class='item' data-value={{typeCode.typeCode}}>
                          {{typeCode.typeCodeDisplayName}}
                        </div>
                      {{/each}}
                    {{/each}}
                  </div>
                </div>
              </div>

              {{#if this.hasDatesError}}
                <div class='ui error message visible'>La date de début de l'acte
                  doit être inférieure à la date de fin de l'acte.</div>
              {{/if}}

              <div class='field'>
                <button
                  type='button'
                  class='ui primary button {{if this.hasDatesError "disabled"}}'
                  {{on 'click' this.onSubmit}}
                  data-test-dmp-search
                >
                  Chercher
                </button>
              </div>
            </div>
          </div>

          {{#if @context.documents}}
            <div class='ui segment border-t-0'>
              <table class='ui unstackable celled striped table'>
                <thead>
                  <tr>
                    <th class='seven wide'>Document</th>
                    <th class='three wide'>Date</th>
                    <th class='two wide'>Type</th>
                    <th class='two wide'>Status</th>
                    <th class='two wide'>Visibilité</th>
                  </tr>
                </thead>

                <tbody>
                  {{#each @context.documents as |document id|}}
                    <tr data-test-id={{id}}>
                      <td data-label='Document'>
                        <LinkButton
                          {{on
                            'click'
                            (fn this.setCurrentDocumentMetadata document)
                          }}
                          data-test-document={{id}}
                        >
                          {{document.title}}
                          {{#if (eq document.classCode '90')}}
                            <span data-tooltip='Document déposé par le patient'>
                              <i class='user icon'></i>
                            </span>
                          {{/if}}
                        </LinkButton>
                      </td>
                      <td data-label='Age'>{{formatDate
                          document.creationTime
                          weekday='long'
                          month='long'
                          day='2-digit'
                          year='numeric'
                          hour='2-digit'
                          minute='2-digit'
                        }}</td>
                      <td
                        data-label='Type de document'
                      >{{this.getClassCodeDisplayName document.classCode}}</td>
                      {{!-- prettier-ignore --}}
                      <td data-label="Status">

              {{! @glint-expect-error }}
                    {{this.getStatusString document.status}}
                  </td>
                      {{!-- prettier-ignore --}}
                      <td data-label="Visibilité">

              {{! @glint-expect-error }}
                    {{this.getVisibilityString document.confidentialities}}
                  </td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          {{/if}}
        </div>
        <Webpsurls @webPSUrls={{@context.webPSUrls}} />
      </:content>
    </PageContainer>
  </template>
}
